/**
 * Command-Palette (Ctrl+K / Cmd+K).
 *
 * Globale Schnellnavigation:
 * - Filter setzen (Filiale, Warengruppe, Artikel, Kassierer, Jahr, Monat, Wochentag)
 * - Seiten wechseln (Dashboard, Pivot, Berichte, Admin)
 * - Favoriten direkt anwenden
 */

import { Command } from 'cmdk';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { Icon } from './Icon.tsx';

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const addFilter = useFilterStore((s) => s.addFilter);
  const setAll = useFilterStore((s) => s.setAll);
  const clearAll = useFilterStore((s) => s.clearAll);

  // Datenquellen für Suche
  const { data: dims } = useQuery({
    queryKey: ['dimensions'],
    queryFn: api.dimensions,
    enabled: open,
  });
  const { data: favs = [] } = useQuery({
    queryKey: ['favorites'],
    queryFn: api.favoritesList,
    enabled: open,
  });

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((o) => !o);
      }
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  function run(action: () => void) {
    action();
    setOpen(false);
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-start justify-center pt-24 animate-fade-in"
      onClick={() => setOpen(false)}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white dark:bg-slate-900 rounded-xl shadow-elevated border border-slate-200 dark:border-slate-700 overflow-hidden animate-slide-up"
      >
        <Command
          loop
          className="w-full"
          filter={(value, search) => {
            const v = value.toLowerCase();
            const s = search.toLowerCase();
            if (v.includes(s)) return 1;
            return 0;
          }}
        >
          <div className="flex items-center px-3 border-b border-slate-200 dark:border-slate-800">
            <Icon name="search" size={16} className="text-slate-400" />
            <Command.Input
              placeholder="Suchen: Filialen, Artikel, Warengruppen, Aktionen …"
              className="flex-1 px-3 py-3 bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
            <kbd className="text-2xs text-slate-400 bg-slate-100 dark:bg-slate-800 rounded px-1.5 py-0.5 font-mono">ESC</kbd>
          </div>
          <Command.List className="max-h-96 overflow-y-auto p-2 scrollbar-thin">
            <Command.Empty className="py-6 text-center text-sm text-slate-400">
              Keine Treffer.
            </Command.Empty>

            {/* Navigation */}
            <Command.Group heading="Navigation" className="text-2xs uppercase tracking-wider text-slate-500 font-semibold px-2 py-1 mt-1 first:mt-0">
              <PaletteItem icon="dashboard" onSelect={() => run(() => navigate('/'))}>
                Dashboard
              </PaletteItem>
              <PaletteItem icon="table" onSelect={() => run(() => navigate('/pivot'))}>
                Pivot-Tabelle
              </PaletteItem>
              <PaletteItem icon="reports" onSelect={() => run(() => navigate('/reports'))}>
                Berichte
              </PaletteItem>
              <PaletteItem icon="reports" onSelect={() => run(() => navigate('/periodenvergleich'))}>
                Periodenvergleich
              </PaletteItem>
            </Command.Group>

            {/* Aktionen */}
            <Command.Group heading="Aktionen" className="text-2xs uppercase tracking-wider text-slate-500 font-semibold px-2 py-1 mt-3">
              <PaletteItem icon="x" onSelect={() => run(() => clearAll())}>
                Alle Filter zurücksetzen
              </PaletteItem>
            </Command.Group>

            {/* Favoriten */}
            {favs.length > 0 && (
              <Command.Group heading="Favoriten" className="text-2xs uppercase tracking-wider text-slate-500 font-semibold px-2 py-1 mt-3">
                {favs.slice(0, 8).map((f) => (
                  <PaletteItem
                    key={`fav-${f.id}`}
                    icon={f.pinned ? 'star-filled' : 'star'}
                    iconClass={f.pinned ? 'text-warning-500' : ''}
                    onSelect={() =>
                      run(() => {
                        setAll(f.filters as never);
                        if (f.path && f.path !== window.location.pathname) navigate(f.path);
                      })
                    }
                  >
                    {f.name}
                    <span className="ml-auto text-2xs text-slate-400">
                      {Object.keys(f.filters).length} Filter
                    </span>
                  </PaletteItem>
                ))}
              </Command.Group>
            )}

            {/* Filter-Werte: top Werte pro Dimension */}
            {dims?.map((dim) => {
              if (!dim.values.length) return null;
              const label = DIM_LABEL[dim.dimension] ?? dim.dimension;
              return (
                <Command.Group
                  key={dim.dimension}
                  heading={label}
                  className="text-2xs uppercase tracking-wider text-slate-500 font-semibold px-2 py-1 mt-3"
                >
                  {dim.values.slice(0, 12).map((v) => (
                    <PaletteItem
                      key={`${dim.dimension}-${v.value}`}
                      icon="filter"
                      onSelect={() => run(() => addFilter(dim.dimension, v.value as never))}
                    >
                      {String(v.label)}
                      <span className="ml-auto text-2xs text-slate-400 tabular-nums">
                        {v.count > 1000 ? `${(v.count / 1000).toFixed(1)}k` : v.count}
                      </span>
                    </PaletteItem>
                  ))}
                </Command.Group>
              );
            })}
          </Command.List>

          <div className="border-t border-slate-200 dark:border-slate-800 px-3 py-2 flex items-center justify-between text-2xs text-slate-400">
            <div className="flex items-center gap-3">
              <span><kbd className="kbd">↑↓</kbd> navigieren</span>
              <span><kbd className="kbd">⏎</kbd> auswählen</span>
            </div>
            <span>
              <kbd className="kbd">⌘K</kbd> öffnet diese Palette
            </span>
          </div>
        </Command>
      </div>
    </div>
  );
}

function PaletteItem({
  icon,
  iconClass = '',
  onSelect,
  children,
}: {
  icon: 'dashboard' | 'table' | 'reports' | 'star' | 'star-filled' | 'filter' | 'x';
  iconClass?: string;
  onSelect: () => void;
  children: React.ReactNode;
}) {
  return (
    <Command.Item
      onSelect={onSelect}
      className="flex items-center gap-2.5 px-2 py-2 rounded-md text-sm cursor-pointer text-slate-700 dark:text-slate-300 aria-selected:bg-brand-50 aria-selected:text-brand-700 dark:aria-selected:bg-brand-950 dark:aria-selected:text-brand-300 transition-colors"
    >
      <Icon name={icon} size={14} className={`text-slate-400 ${iconClass}`} />
      <span className="flex-1 flex items-center gap-2 truncate">{children}</span>
    </Command.Item>
  );
}

const DIM_LABEL: Record<string, string> = {
  store_name: 'Filialen',
  store_number: 'Filial-Nrn',
  warengruppe_name: 'Warengruppen',
  artikel_bezeichnung: 'Artikel',
  kassierer_name: 'Kassierer',
  plz: 'PLZ',
  ort: 'Orte',
  jahr: 'Jahre',
  monat: 'Monate',
  wochentag: 'Wochentage',
};
